import { useEffect, useMemo, useState } from "react";

import { Box, Button } from "@mui/material";
import { GridColDef, GridRowModel } from "@mui/x-data-grid";
import AddIcon from '@mui/icons-material/Add';
import { useLazyQuery } from "@apollo/client";
import { FetchMoreCallbackArgs, SearchBar, TablePaginated, TableRemoveButton } from "ui";
import { FragmentType, getFragmentData, gql } from "../../../__generated__";
import { canAddStudentAndStaff } from "ft-shared-graphql";
import usePersonAuthStore from "../../store/personAuthStore";

export const DEFAULT_PAGE_SIZE = 3;

const TABLE_COLUMNS: GridColDef[] = [
    { field: "firstName", headerName: "First Name", flex: 1, sortable: true },
    { field: "lastName", headerName: "Last Name", flex: 1, sortable: true },
    // { field: "name", headerName: "User Name", flex: 1, sortable: false },
    { field: "role", headerName: "Role", flex: 1, sortable: false },
];

export const STUDENT_PERSONS_LINK_PREVIEW_QUERY = gql(`
 query StudentPersonsLinkPreview($studentId: UUID!, $query:String,$first:Int, $after:String ) {
        student(id: $studentId) {
            __typename
            id
            references
            persons(status: ACTIVE, query:$query,first:$first,after: $after ) {
            pageInfo{
                  startCursor
                endCursor
                hasNextPage
                hasPreviousPage
            }  
            edges{
                cursor
                node {
                  ..._studentPerson
                }
              }
            totalCount
            }
        }
    }`);

const STUDENT_PERSONS_PERSON_FRAGMENT = gql(`
    fragment _studentPerson on Person {
        __typename
        id
        firstName
        lastName
        role {
            name
            normalizedName
            clientFacingName
        }
}
`);

const mapQueryToTable = (data: any): GridRowModel => {
    const fragment = getFragmentData(STUDENT_PERSONS_PERSON_FRAGMENT, data.node);
    return {
        ...fragment,
        name: `${fragment.lastName} ${fragment.firstName}`,
        role: fragment.role?.clientFacingName,
        normalizedRole: fragment.role?.normalizedName
    } as const
}

export interface StudentTableLinkPreviewProps {
    studentId: string,
    removeRowId: string | null,
    onLink: (id: string) => void
    onRemove: (id: string) => void
    onDisabled: (reason: 'NOT_ENOUGH_REFERENCES' | 'UNKNOWN') => void
}

const StudentTableLinkPreview = ({
    studentId,
    removeRowId,
    onLink,
    onRemove,
    onDisabled
}: StudentTableLinkPreviewProps) => {
    const [fetchRecords, { data, loading, error, fetchMore, refetch }] = useLazyQuery(STUDENT_PERSONS_LINK_PREVIEW_QUERY, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {
            studentId,
            first: DEFAULT_PAGE_SIZE,
            query: ""
        }
    });

    // const [pageTableState, setPageTableState] = useState<{ columns: GridColDef[], rows: GridRowModel[] }>({
    //     columns: [],
    //     rows: []
    // });
    const [pageEdges, setPageEdges] = useState<any[] | null>(null);
    const roles = usePersonAuthStore(state => state.roles)
    const rows = useMemo(() => {
        const edges = pageEdges ?? data?.student.persons?.edges ?? []
        return edges.map(mapQueryToTable);
    }, [pageEdges, data]);

    useEffect(() => {
        // if (selectedPerson) return;
        async function fetchResults() {
            await fetchRecords();
        }
        fetchResults();
    }, [])
    const columns: GridColDef[] = useMemo(() => {

        const cols = [...TABLE_COLUMNS]
        if (canAddStudentAndStaff(roles)) {
            cols.push({
                field: 'remove',
                headerName: 'Remove',
                align: 'center',
                sortable: false,
                width: 100,
                renderCell: (params) => {
                    return (
                        <TableRemoveButton
                            id={params.row.id}
                            removing={params.row.id === removeRowId}
                            onRemove={onRemove}
                            onDisabled={() => {
                                if (((data?.student.references ?? 0) <= 1)) {
                                    return onDisabled("NOT_ENOUGH_REFERENCES");
                                }

                                onDisabled("UNKNOWN")
                            }}
                            disabled={(data?.student.references ?? 0) <= 1} />
                    )
                }
            } as GridColDef);
        }
        return cols
    }, [data])
    // useEffect(() => {
    //     if (!studentId) return;
    //     // TODO -- Make student edge fragment generic. For now ignore TypeScript error
    //     setPageTableState(prevState => {
    //         const rows = data?.student.persons?.edges?.map(mapQueryToTable) ?? []
    //         return {
    //             ...prevState,
    //             rows
    //         }
    //     });
    // }, [studentId, loading, data])

    // useEffect(() => {
    //     if (!onRemove) return;

    //     setPageTableState(prevState => {
    //         const columns = [...TABLE_COLUMNS, {
    //             field: 'remove',
    //             headerName: 'Remove',
    //             align: 'center',
    //             sortable: false,
    //             width: 100,
    //             renderCell: (params) => {
    //                 return (
    //                     <TableRemoveButton
    //                         id={params.row.id}
    //                         removing={params.row.id === removeRowId}
    //                         onRemove={onRemove}
    //                         onDisabled={() => {
    //                             if ((data?.student.references ?? 0) <= 1) {
    //                                 return onDisabled("NOT_ENOUGH_REFERENCES");
    //                             }

    //                             onDisabled("UNKNOWN")
    //                         }}
    //                         disabled={(data?.student.references ?? 0) <= 1} />
    //                 )
    //             }
    //         } as GridColDef];

    //         return {
    //             ...prevState,
    //             columns
    //         }
    //     })
    // }, [onRemove, data]);

    const handleLink = (id: string) => {
        if (!onLink) return;
        onLink(id)
    }
    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        try {
            const res = await fetchMore({
                variables: {
                    studentId: studentId,
                    query: variables.query ?? '',
                    first: variables.first ?? DEFAULT_PAGE_SIZE,
                    after: variables.after ?? null,

                }
            })
            setPageEdges(res?.data?.student.persons?.edges ?? [])
        } catch (err) {
            console.log("there is an error: ", err);
        }
    }
    return (
        <TablePaginated
            loading={loading}
            renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                return (
                    <Box component="div">
                        <SearchBar
                            value={searchQuery}
                            clearSearch={onSearchClear}
                            onChange={onSearch}>
                            <Button
                                sx={{ display: canAddStudentAndStaff(roles) ? "block" : "none" }}
                                color="secondary"
                                onClick={x => handleLink(studentId ?? '')}
                                startIcon={
                                    <AddIcon
                                        style={{
                                            backgroundColor: "#253D93",
                                            borderRadius: "100%",
                                            color: "white",
                                        }}
                                    />
                                }
                            >
                                Link Users
                            </Button>
                        </SearchBar>
                    </Box>
                )
            }}
            columns={columns}
            rows={rows}
            pageInfo={data?.student?.persons?.pageInfo}
            pageSize={DEFAULT_PAGE_SIZE}
            totalRows={data?.student?.persons?.totalCount ?? 0}
            disableBoxShadow={true}
            onFetchMore={handleFetchMore}
        />
    )
    // return (
    //     <Table
    //         loading={loading}
    //         renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
    //             return (
    //                 <Box component="div">
    //                     <SearchBar
    //                         value={searchQuery}
    //                         clearSearch={onSearchClear}
    //                         onChange={onSearch}>
    //                         <Button
    //                             color="secondary"
    //                             onClick={x => handleLink(studentId ?? '')}
    //                             startIcon={
    //                                 <AddIcon
    //                                     style={{
    //                                         backgroundColor: "#253D93",
    //                                         borderRadius: "100%",
    //                                         color: "white",
    //                                     }}
    //                                 />
    //                             }
    //                         >
    //                             Link Users
    //                         </Button>
    //                     </SearchBar>
    //                 </Box>
    //             )
    //         }}
    //         columns={pageTableState.columns}
    //         rows={pageTableState.rows}
    //         pageSize={5}
    //         disableBoxShadow={true}
    //     />
    // )
}

export default StudentTableLinkPreview;